import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { MSym } from '@storeo/ui';
import { LUMINA_TEMPLATES } from '@storeo/templates';
import { api } from '../../lib/api';
import { TemplatePreview, type PreviewDraft } from '../../components/editor/TemplatePreview';
import { StudioControlBar } from '../../components/editor/StudioControlBar';
import { StudioPanel, type PanelTab } from '../../components/editor/StudioPanel';
import { BrowserFrame, type Device } from '../../components/editor/BrowserFrame';
import { DemoBanner, PlaceholderTag } from '../../components/lumina/Placeholder';

const ROOT_DOMAIN = import.meta.env.VITE_STOREFRONT_ROOT_DOMAIN ?? 'storeo.com.br';
function storeUrl(subdomain: string): string {
  const scheme =
    ROOT_DOMAIN.includes('lvh.me') || ROOT_DOMAIN.includes('localhost') ? 'http' : 'https';
  return `${scheme}://${subdomain}.${ROOT_DOMAIN}`;
}

const SECTION_LABEL: Record<string, string> = {
  feat: 'Destaque',
  grid: 'Catálogo',
  quote: 'Depoimento',
};
const ACCENT_SWATCHES = ['#4edea3', '#4cd7f6', '#8083ff', '#f59e0b', '#f0616d', '#c0c1ff'];

/** O Studio só oferece os 6 modelos Lumina. Loja num template legado (minimalista/comunidade/
 *  oferta) ou desconhecido cai no `lumina-cinema` — assim o card certo aparece ativo e o Salvar
 *  grava um valor que o storefront renderiza com a réplica (senão o legado continua no ar). */
const LUMINA_KEYS = new Set(Object.keys(LUMINA_TEMPLATES));
function normalizeTemplate(t: string): string {
  return LUMINA_KEYS.has(t) ? t : 'lumina-cinema';
}

type Section = { key: string; visible: boolean };

const INPUT =
  'w-full px-space-md py-space-sm rounded-lg bg-surface-container-lowest text-on-surface font-body-md text-body-md placeholder:text-outline focus:outline-none focus:bg-surface-container transition-all shadow-inner';

/** P9 · Studio — editor de loja (M7, ADR 0018). Redesign do shell pra bater com
 *  docs/prototype/lpplat/construtor/ (dentro da Shell do painel). Nesta entrega o template
 *  Lumina Cinema é réplica fiel/verbatim do protótipo; a costura de dados reais (cores,
 *  logo, seções) e o editor de checkout vêm depois. Save/Publish seguem funcionais. */
export function Loja() {
  const qc = useQueryClient();
  const store = useQuery({ queryKey: ['store'], queryFn: () => api.store() });
  const products = useQuery({ queryKey: ['products'], queryFn: () => api.products() });

  const [tab, setTab] = useState<PanelTab>('Templates');
  const [template, setTemplate] = useState('lumina-cinema');
  const [primaryColor, setPrimaryColor] = useState('#0a0e17');
  const [accentColor, setAccentColor] = useState('#4edea3');
  const [tagline, setTagline] = useState('');
  const [sections, setSections] = useState<Section[]>([]);
  const [testimonialQuote, setTestimonialQuote] = useState('');
  const [testimonialAuthor, setTestimonialAuthor] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [device, setDevice] = useState<Device>('desktop');
  const [canvasView, setCanvasView] = useState<'vitrine' | 'produto'>('vitrine');
  const [panelOpen, setPanelOpen] = useState(true);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!store.data) return;
    setTemplate(normalizeTemplate(store.data.template));
    setPrimaryColor(store.data.primaryColor);
    setAccentColor(store.data.accentColor);
    setTagline(store.data.tagline ?? '');
    setSections(store.data.sections);
    setTestimonialQuote(store.data.testimonialQuote ?? '');
    setTestimonialAuthor(store.data.testimonialAuthor ?? '');
    setLogoUrl(store.data.logoUrl);
  }, [store.data]);

  const togglePublish = useMutation({
    mutationFn: () =>
      store.data?.status === 'published' ? api.unpublishStore() : api.publishStore(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['store'] }),
  });

  const save = useMutation({
    mutationFn: async () => {
      if (logoFile) await api.uploadLogo(logoFile);
      await Promise.all([
        api.updateBranding({ template, primaryColor, accentColor, tagline: tagline || undefined }),
        api.updateCustomization({
          sections,
          testimonialQuote: testimonialQuote || undefined,
          testimonialAuthor: testimonialAuthor || undefined,
        }),
      ]);
    },
    onSuccess: () => {
      setSavedAt(new Date());
      setLogoFile(null);
      setError(null);
      qc.invalidateQueries({ queryKey: ['store'] });
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Não foi possível salvar'),
  });

  function move(index: number, dir: -1 | 1) {
    setSections((cur) => {
      const next = [...cur];
      const target = index + dir;
      if (target < 0 || target >= next.length) return cur;
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });
  }
  function toggleSection(key: string) {
    setSections((cur) =>
      cur.map((sec) => (sec.key === key ? { ...sec, visible: !sec.visible } : sec)),
    );
  }

  if (!store.data) {
    return (
      <div className="lumina text-on-surface">
        <h1 className="font-headline-lg text-headline-lg">Studio</h1>
      </div>
    );
  }

  const published = store.data.status === 'published';
  const host = storeUrl(store.data.subdomain).replace(/^https?:\/\//, '');
  const draft: PreviewDraft = {
    name: store.data.name,
    tagline,
    primaryColor,
    accentColor,
    logoUrl: logoFile ? URL.createObjectURL(logoFile) : logoUrl,
    sections,
    testimonialQuote,
    testimonialAuthor,
    template,
  };

  const estiloSlot = (
    <div className="flex flex-col gap-space-md rounded-xl bg-surface-container-low p-space-md">
      <div>
        <span className="font-label-sm text-label-sm text-on-surface-variant font-medium block mb-space-2xs">
          Logo
        </span>
        <div className="flex items-center gap-space-sm">
          {draft.logoUrl ? (
            <img
              className="w-12 h-12 rounded-lg object-contain bg-surface-container"
              src={draft.logoUrl}
              alt=""
            />
          ) : (
            <span className="w-12 h-12 rounded-lg bg-surface-container" />
          )}
          <input
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            className="text-body-sm font-body-sm text-on-surface-variant"
            onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
          />
        </div>
      </div>
      <label className="flex flex-col gap-space-2xs font-label-sm text-label-sm text-on-surface-variant font-medium">
        Cor de fundo
        <input
          type="color"
          className="h-10 w-full rounded-lg bg-surface-container-lowest"
          value={primaryColor}
          onChange={(e) => setPrimaryColor(e.target.value)}
        />
      </label>
      <div className="flex flex-col gap-space-2xs">
        <span className="font-label-sm text-label-sm text-on-surface-variant font-medium">
          Cor de destaque
        </span>
        <div className="flex flex-wrap items-center gap-space-xs">
          {ACCENT_SWATCHES.map((hex) => (
            <button
              key={hex}
              type="button"
              onClick={() => setAccentColor(hex)}
              aria-label={hex}
              className={`w-8 h-8 rounded-lg ${accentColor.toLowerCase() === hex ? 'ring-2 ring-white' : ''}`}
              style={{ background: hex }}
            />
          ))}
          <input
            type="color"
            className="h-8 w-8 rounded-lg bg-surface-container-lowest"
            value={accentColor}
            onChange={(e) => setAccentColor(e.target.value)}
          />
        </div>
      </div>
      <label className="flex flex-col gap-space-2xs font-label-sm text-label-sm text-on-surface-variant font-medium">
        Frase de efeito
        <input
          className={INPUT}
          value={tagline}
          maxLength={140}
          onChange={(e) => setTagline(e.target.value)}
        />
      </label>
      <div className="flex items-center gap-space-xs font-label-sm text-label-sm text-on-surface-variant">
        Tipografia por template <PlaceholderTag label="travada" />
      </div>
    </div>
  );

  const secoesSlot = (
    <div className="flex flex-col gap-space-sm rounded-xl bg-surface-container-low p-space-md">
      <span className="font-label-sm text-label-sm text-on-surface-variant">
        Olho pra ocultar · setas pra reordenar
      </span>
      <div className="flex flex-col gap-space-2xs">
        <div className="flex items-center gap-space-sm p-space-sm rounded-lg bg-surface-container opacity-60">
          <MSym name="lock" className="text-[16px]" />
          <span className="font-label-md text-label-md">Cabeçalho</span>
        </div>
        {sections.map((sec, i) => (
          <div
            key={sec.key}
            className={`flex items-center gap-space-2xs p-space-sm rounded-lg bg-surface-container ${sec.visible ? '' : 'opacity-50'}`}
          >
            <button
              type="button"
              disabled={i === 0}
              onClick={() => move(i, -1)}
              className="text-on-surface-variant disabled:opacity-30"
              aria-label="Subir"
            >
              <MSym name="keyboard_arrow_up" className="text-[18px]" />
            </button>
            <button
              type="button"
              disabled={i === sections.length - 1}
              onClick={() => move(i, 1)}
              className="text-on-surface-variant disabled:opacity-30"
              aria-label="Descer"
            >
              <MSym name="keyboard_arrow_down" className="text-[18px]" />
            </button>
            <span className="font-label-md text-label-md flex-1">
              {SECTION_LABEL[sec.key] ?? sec.key}
            </span>
            <button
              type="button"
              onClick={() => toggleSection(sec.key)}
              className={sec.visible ? 'text-primary' : 'text-outline'}
              aria-label={sec.visible ? 'Ocultar' : 'Mostrar'}
            >
              <MSym name={sec.visible ? 'visibility' : 'visibility_off'} className="text-[18px]" />
            </button>
          </div>
        ))}
        <div className="flex items-center gap-space-sm p-space-sm rounded-lg bg-surface-container opacity-60">
          <MSym name="lock" className="text-[16px]" />
          <span className="font-label-md text-label-md">Rodapé</span>
        </div>
      </div>
      {sections.some((sec) => sec.key === 'quote' && sec.visible) ? (
        <div className="flex flex-col gap-space-2xs pt-space-2xs">
          <textarea
            className={`${INPUT} h-auto`}
            rows={2}
            placeholder="“Mudou minha vida…”"
            value={testimonialQuote}
            onChange={(e) => setTestimonialQuote(e.target.value)}
          />
          <input
            className={INPUT}
            placeholder="Autor — ex.: Rafael M."
            value={testimonialAuthor}
            onChange={(e) => setTestimonialAuthor(e.target.value)}
          />
        </div>
      ) : null}
    </div>
  );

  const checkoutSlot = (
    <div className="flex flex-col gap-space-md">
      <DemoBanner>
        O editor de checkout transparente (campos, cores, order bump) está desenhado — integração em
        breve.
      </DemoBanner>
      <div className="rounded-xl bg-surface-container-low p-space-md flex flex-col gap-space-sm">
        {[
          'Cores & campos do checkout',
          'Order bump no pagamento',
          'Prova social ao vivo',
          'Topbar de urgência & PIX',
        ].map((b) => (
          <div
            key={b}
            className="flex items-center justify-between p-space-sm rounded-lg bg-surface-container"
          >
            <span className="font-label-md text-label-md text-on-surface">{b}</span>
            <PlaceholderTag />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="lumina flex flex-col gap-space-md text-on-surface">
      <StudioControlBar
        storeName={store.data.name}
        host={host}
        storeHref={storeUrl(store.data.subdomain)}
        canvasView={canvasView}
        onCanvasView={setCanvasView}
        device={device}
        onDevice={setDevice}
        published={published}
        saving={save.isPending}
        savedAt={savedAt}
        onSave={() => save.mutate()}
        onPublish={() => togglePublish.mutate()}
        publishing={togglePublish.isPending}
        panelOpen={panelOpen}
        onTogglePanel={() => setPanelOpen((v) => !v)}
      />

      {error ? (
        <p className="rounded-lg bg-error-container/25 text-on-error-container px-space-md py-space-sm font-body-sm text-body-sm">
          {error}
        </p>
      ) : null}

      <div className="flex flex-col lg:flex-row gap-space-md items-start">
        <StudioPanel
          tab={tab}
          onTab={setTab}
          template={template}
          onTemplate={setTemplate}
          estiloSlot={estiloSlot}
          secoesSlot={secoesSlot}
          checkoutSlot={checkoutSlot}
          open={panelOpen}
        />

        <BrowserFrame host={host} device={device}>
          <TemplatePreview
            draft={draft}
            products={products.data ?? []}
            subdomain={store.data.subdomain}
            view={canvasView}
          />
        </BrowserFrame>
      </div>
    </div>
  );
}
